import { useState, useEffect, useRef } from 'react';
import Navigation from './components/Navigation';
import WebGLViewer from './components/WebGLViewer';
import { hotspots } from './data/hotspots';
import './App.css';

function App() {
  const [view, setView] = useState('home');       // 'home' | 'hotspots' | 'groups' | 'group-detail'
  const [activeId, setActiveId] = useState(null);         // selected hotspot id
  const [activeGroupId, setActiveGroupId] = useState(null); // selected group id
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);
  const programmaticClick = useRef(false);

  const triggerWebGL = (id) => {
    const attempt = () => {
      const iframe = document.getElementById('webgl-frame');
      if (!iframe?.contentDocument) return false;
      const elem = iframe.contentDocument.getElementById(id);
      if (!elem) return false;
      programmaticClick.current = true;
      elem.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: iframe.contentWindow }));
      setTimeout(() => { programmaticClick.current = false; }, 300);
      return true;
    };
    if (!attempt()) {
      setTimeout(() => { if (!attempt()) setTimeout(attempt, 500); }, 200);
    }
  };

  // "Warehouse" button clicked → enter hotspot list + reset camera
  const handleWarehouseClick = () => {
    setView('hotspots');
    setActiveId(null);
    setActiveGroupId(null);
    triggerWebGL('button_home');
  };

  // Hotspot clicked → show its groups + move camera
  const handleHotspotSelect = (id) => {
    setActiveId(id);
    setActiveGroupId(null);
    setView('groups');
    triggerWebGL(id);
  };

  // Group item clicked → show full description
  const handleGroupSelect = (groupId) => {
    setActiveGroupId(groupId);
    setView('group-detail');
  };

  // Back from hotspot list → home + reset camera
  const handleBackToHome = () => {
    setView('home');
    setActiveId(null);
    setActiveGroupId(null);
    triggerWebGL('button_home');
  };

  // Back from group list → hotspot list (keep camera position)
  const handleBackToHotspots = () => {
    setActiveGroupId(null);
    setView('hotspots');
  };

  // Back from group detail → group list
  const handleBackToGroups = () => {
    setActiveGroupId(null);
    setView('groups');
  };

  // Sync when WebGL fires a hotspot click (e.g. user clicks annotation in 3D scene)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'HOME') {
        if (!programmaticClick.current) {
          setView('home');
          setActiveId(null);
          setActiveGroupId(null);
        }
      }
      if (event.data?.type === 'HOTSPOT_ACTIVE') {
        const id = event.data.id || null;
        if (id) {
          setActiveId(id);
          setActiveGroupId(null);
          setView('groups');
        }
      }
      if (event.data?.type === 'FULLSCREEN') {
        const elem = document.documentElement;
        if (!document.fullscreenElement) {
          elem.requestFullscreen();
          setIsFullscreen(true);
          setSidebarOpen(true);
          event.source.postMessage({ type: 'FS_STATE', value: true }, '*');
        } else {
          document.exitFullscreen();
          setIsFullscreen(false);
          event.source.postMessage({ type: 'FS_STATE', value: false }, '*');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const handleFsChange = () => {
      const fsActive = !!document.fullscreenElement;
      setIsFullscreen(fsActive);
      if (fsActive) setSidebarOpen(true);
      const iframe = document.getElementById('webgl-frame');
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage({ type: 'FS_STATE', value: fsActive }, '*');
      }
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  // Sync sidebar when user clicks an annotation directly in the 3D scene.
  // Uses a single delegated listener on the iframe document (capture phase) so it
  // survives element recreation by the WebGL engine and never needs re-attachment.
  useEffect(() => {
    const iframe = document.getElementById('webgl-frame');
    if (!iframe) return;

    const hotspotIds = new Set(hotspots.map(h => h.id));
    let delegated = false;
    let pollInterval;

    const attachDelegation = () => {
      const doc = iframe.contentDocument;
      if (!doc?.body) return false;
      if (delegated) return true;
      delegated = true;

      doc.addEventListener('click', (e) => {
        if (programmaticClick.current) return;
        // Walk up the DOM to find a matching hotspot or home button
        let target = e.target;
        while (target && target !== doc.documentElement) {
          if (target.id === 'button_home') {
            setView('home');
            setActiveId(null);
            setActiveGroupId(null);
            return;
          }
          if (target.id && hotspotIds.has(target.id)) {
            setActiveId(target.id);
            setActiveGroupId(null);
            setView('groups');
            return;
          }
          target = target.parentElement;
        }
      }, true); // capture phase — fires before any WebGL handler that might stopPropagation

      return true;
    };

    const onLoad = () => {
      delegated = false; // reset on each reload so delegation re-attaches
      clearInterval(pollInterval);
      pollInterval = setInterval(() => {
        if (attachDelegation()) clearInterval(pollInterval);
      }, 300);
      setTimeout(() => clearInterval(pollInterval), 30000);
    };

    iframe.addEventListener('load', onLoad);
    // Try immediately in case the iframe already loaded before this effect ran
    onLoad();

    return () => {
      iframe.removeEventListener('load', onLoad);
      clearInterval(pollInterval);
    };
  }, []);


  return (
    <div className={`app-container${isFullscreen ? ' app-container--fullscreen' : ''}`}>

      {/* Left: Navigation Panel */}
      <aside className={`sidebar${sidebarOpen ? '' : ' sidebar--collapsed'}`}>
        <div className="sidebar__header">
          <img src="/media/MHI.webp" alt="MHI Logo" className="sidebar__logo" />
        </div>

        <div className="sidebar__title">Material Handling Facilitites</div>

        <Navigation
          view={view}
          activeId={activeId}
          activeGroupId={activeGroupId}
          onWarehouseClick={handleWarehouseClick}
          onHotspotSelect={handleHotspotSelect}
          onGroupSelect={handleGroupSelect}
          onBackToHome={handleBackToHome}
          onBackToHotspots={handleBackToHotspots}
          onBackToGroups={handleBackToGroups}
        />
      </aside>

      {/* Sidebar toggle — circular button fixed at sidebar edge */}
      <button
        className="sidebar__toggle"
        style={{ left: sidebarOpen ? '338px' : '0px' }}
        onClick={() => setSidebarOpen(o => !o)}
      >
        {sidebarOpen ? '←' : '→'}
      </button>

      {/* Right: WebGL Viewer */}
      <main className="viewer" ref={iframeRef}>
        <WebGLViewer />
      </main>

    </div>
  );
}

export default App;
